/**
 * A "multi-block" manipulates multiple query blocks together, for instance
 * multiple filters.
 */
import {Component} from 'panel';

import {QueryReason} from 'common/report/util/query-tracking/types';
import {filterAttributes} from 'common/util';
import {removeByValue, replaceByIndex} from 'common/util/array';
import {emitTrackEvent, preventTracking, propagateEvent} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {toSentenceCase} from 'common/util/string';
import {QueryBlock} from 'common/widgets/query-block';
import {SELECTOR_TYPE, THEME} from 'common/widgets/query-block/constants';
import {getSegmentType} from 'common/widgets/query-block/segment-menu/types';
import {ResourceType} from 'common/widgets/property-filter-menu/models/types';

import template from './index.jade';
import css from './index.styl?inline';

interface QueryMultiBlockState {
  addingBlock: boolean;
  prefixRenderer: any;
  // this has the same effect as this.state.selectionObject in query-builder. Read there for context
  stagingSelected: Array<Record<string, any>>;
}

export class QueryMultiBlock extends Component<QueryMultiBlockState> {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'adding-disabled': `boolean`,
      'allow-cohort-filter': `boolean`,
      'allow-typecast': `boolean`,
      'all-data-groups': `boolean`,
      'block-prefix': `string`,
      'button-icon': {
        type: `string`,
        default: `plus`,
      },
      'enable-enhanced-list-filters': `boolean`,
      'not-button-input-only': `boolean`,
      'button-open': `boolean`,
      'button-text': {type: `string`, default: null},
      'button-selector-type': {type: `string`, default: SELECTOR_TYPE.BUTTON, enum: Object.values(SELECTOR_TYPE)},
      'button-selector-theme': {type: `string`, default: THEME.SECONDARY, enum: Object.values(THEME)},
      'data-group-id': `string`,
      'enable-workspace-filter-restrictions': `boolean`,
      'event-names': {type: `json`, default: null},
      'exclude-custom-events': `boolean`,
      'exclude-dimension-properties': {
        type: `boolean`,
        description: `whether to hide dimension properties from properties menus`,
      },
      'include-custom-properties': {
        type: `string`,
        enum: [`none`, `persisted-only`, `persisted-non-behavioral-only`, `all`],
        default: `none`,
      },
      'first-block-prefix': `string`,
      layout: {type: `string`, enum: [`row`, `column`], default: `row`},
      limit: {type: `number`, default: null},
      'other-group-by-options': {type: `string`, default: `[]`},
      'property-combine-mode': {type: `string`, default: null},
      'project-has-cohorts': `boolean`,
      'hide-events': `boolean`,
      'project-has-people': `boolean`,
      'project-id': {type: `number`, default: null},
      'hide-edit-controls': {
        type: `boolean`,
        description: `show controls that popup modals to edit entities like custom events and properties`,
      },
      'show-special-date-property': `boolean`,
      'hide-special-date-property-unit': `boolean`,
      selected: {type: `json`, default: []},
      'show-special-dataset-property': `boolean`,
      'selector-type': QueryBlock.attrsSchema[`selector-type`],
      theme: {type: `string`, default: THEME.PRIMARY, enum: Object.values(THEME)},
      type: {type: `string`, default: null},
      'tooltip-data': `json`,
      'tooltip-data-index': {
        type: `number`,
        default: null,
        description: `defines which block displays the context panel`,
      },
      'user-id': `number`,
      'support-b2b-cohorts': `boolean`,
      'inline-cohorts-override': `boolean`,
      'track-name': {
        type: `string`,
        description: `If specified, dispatches '[mp-query-builder] <event>' bubbled events that can be listened for tracking with "Report name" as a property`,
      },
      'workspace-id': `number`,
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,

      defaultState: {
        addingBlock: false,
        prefixRenderer: null,
        stagingSelected: [],
      },

      helpers: {
        preventTracking,
        propagateEvent: (ev) => propagateEvent(this.app, ev),
        buttonText: () => this.attr(`button-text`) || toSentenceCase(this.attr(`type`)) || ``,
        canAddMore: () => !Number.isInteger(this.attr(`limit`)) || this.attr(`limit`) > this.attr(`selected`).length,
        removeSelection: (selectionValue: Obj) => this.updateItems(`remove`, {selectionValue}),
        setAddingBlock: (addingBlock) => this.update({addingBlock}),
        onAddItemBlockClose: () => {
          this.helpers.setAddingBlock(false);
          this.dispatchEvent(new CustomEvent(`close`));
        },

        modifySelection: (index, ev) => {
          const {trackingProps, queryReason} = ev.detail;
          const item = this.extractItemFromEvent(ev);
          this.updateItems(`edit`, {item, index, trackingProps, queryReason});
        },

        selectionIsCohort: (selection) => getSegmentType(selection) === ResourceType.Cohort,

        commitNewBlock: (ev) => {
          const {trackingProps, queryReason} = ev.detail;
          const item = this.extractItemFromEvent(ev);

          this.helpers.setAddingBlock(false);
          this.updateItems(`add`, {item, trackingProps, queryReason});
        },

        getQueryBlockAttrs: (attrs) => {
          return filterAttributes({
            type: this.attr(`type`),
            'allow-cohort-filter': this.attr(`allow-cohort-filter`),
            'allow-typecast': this.attr(`allow-typecast`),
            'all-data-groups': this.attr(`all-data-groups`),
            'data-group-id': this.attr(`data-group-id`),
            'enable-enhanced-list-filters': this.attr(`enable-enhanced-list-filters`),
            'enable-workspace-filter-restrictions': this.attr(`enable-workspace-filter-restrictions`),
            'event-names': JSON.stringify(this.attr(`event-names`)),
            'exclude-custom-events': this.attr(`exclude-custom-events`),
            'exclude-dimension-properties': this.attr(`exclude-dimension-properties`),
            'include-custom-properties': this.attr(`include-custom-properties`),
            'header-text': ``,
            'other-group-by-options': this.attr(`other-group-by-options`),
            'property-combine-mode': this.attr(`property-combine-mode`),
            'project-has-cohorts': this.attr(`project-has-cohorts`),
            'hide-events': this.attr(`hide-events`),
            'project-has-people': this.attr(`project-has-people`),
            'hide-edit-controls': this.attr(`hide-edit-controls`),
            'show-special-date-property': this.attr(`show-special-date-property`),
            'hide-special-date-property-unit': this.attr(`hide-special-date-property-unit`),
            'show-special-dataset-property': this.attr(`show-special-dataset-property`),
            'project-id': this.attr(`project-id`),
            'selector-type': this.attr(`selector-type`),
            theme: this.attr(`theme`),
            'user-id': this.attr(`user-id`),
            'support-b2b-cohorts': this.attr(`support-b2b-cohorts`),
            'inline-cohorts-override': this.attr(`inline-cohorts-override`),
            'track-name': this.attr(`track-name`),
            'workspace-id': this.attr(`workspace-id`),
            ...attrs,
          });
        },
      },
    };
  }

  set prefixRenderer(prefixRenderer) {
    this.update({prefixRenderer});
  }

  public attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    if (attr === `selected`) {
      this.update({
        stagingSelected: this.attr(`selected`),
      });
    }
  }

  private extractItemFromEvent(ev) {
    if (this.attr(`type`) === `cohorts`) {
      return ev.detail.selected;
    }
    return ev.detail.item;
  }

  private updateItems(
    action,
    {
      item,
      index,
      selectionValue,
      trackingProps = {},
      queryReason,
    }: {
      item?: any;
      index?: number;
      newIndex?: number;
      selectionValue?: Obj;
      trackingProps?: Obj;
      queryReason?: QueryReason;
    },
  ) {
    let selections = this.state.stagingSelected || [];
    switch (action) {
      case `add`:
        selections = [...selections, item];
        break;
      case `edit`:
        selections = replaceByIndex(selections, index, item);
        break;
      case `remove`:
        item = selectionValue;
        // note we can't remove by index here. This is because within a frame, due to callbacks, all
        // selections in the array may need to remove themselves. For a 2-array, 'remove 0' and
        // 'remove 1' from children would have 'remove 1' fail due to us having a proper view
        // here due to the buffered selection object but the children underneath having an outdated
        // one as the drawing cycle has not completed yet.
        selections = removeByValue(selections, selectionValue);
        break;
    }
    const {eventName, props} = this.trackingProps(action, item, trackingProps);
    this.update({stagingSelected: selections});
    const detail = {
      items: selections,
      queryReason: queryReason || QueryReason.QbOtherUpdate,
    };
    this.dispatchEvent(new CustomEvent(`update`, {detail}));
    emitTrackEvent(this, {eventName, props, eventPrefix: null, cancelable: true});
  }

  // Generates tracking properties for a given configuration of the component
  private trackingProps(action, item, trackingProps) {
    const menuType = this.attr(`type`);
    let eventName = ``;
    let props = trackingProps || {};

    // TODO delegate this to the child somehow
    switch (menuType) {
      case `breakdown`:
        eventName = `${toSentenceCase(action)} event/property breakdown`;
        props = {
          'Event name': item.event.label,
          'Property data type': item.property.type,
          'Property name': item.property.label,
          'Property raw name': item.property.name,
        };
        break;
      case `properties`:
        eventName = `${toSentenceCase(action)} property`;
        props = {
          'Property data type': item.type,
          'Property name': item.label,
          'Property raw name': item.name,
        };
        break;
      case `filter`:
        eventName = `${toSentenceCase(action)} property filter`;
        break;
      case `segment`:
        eventName = `${toSentenceCase(action)} segment`;
        break;
      case `events`:
        eventName = `${toSentenceCase(action)} event name`;
        props = {
          'Event name': item.label,
          'Event raw name': item.name,
        };
        break;
      default:
        throw new Error(`tracking not defined for mp-query-multi-block configuration`);
    }

    return {eventName, props};
  }
}

export const QueryMultiBlockEl = defineAndMakeJsxFactory(`mp-query-multi-block`, QueryMultiBlock);
